import React, { useState, useEffect, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../../../firebase';
import { useAuth } from '../../../auth/components/AuthProvider';

const LIMIT = 100;

function formatDate(timestamp) {
  if (timestamp == null) return '—';
  if (typeof timestamp === 'string') {
    try {
      return new Date(timestamp).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return '—';
    }
  }
  if (typeof timestamp?.toDate === 'function') {
    return timestamp.toDate().toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' });
  }
  return '—';
}

function isPermissionError(err) {
  if (!err) return false;
  const code = err.code || err?.name || '';
  const msg = (err.message || '').toLowerCase();
  return (
    code === 'permission-denied' ||
    code === 'PERMISSION_DENIED' ||
    msg.includes('permission') ||
    msg.includes('insufficient permissions') ||
    msg.includes('missing or insufficient')
  );
}

export default function AdminMetricasBusqueda() {
  const { user, rol, loading: authLoading } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [soloNoLogueados, setSoloNoLogueados] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user || rol !== 'admin') {
      setError('Tu usuario no tiene permisos para ver las métricas de búsqueda.');
      setLoading(false);
      setLogs([]);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const getSearchLogs = httpsCallable(getFunctions(app), 'getSearchLogs');
        const result = await getSearchLogs({ limit: LIMIT });
        const data = result?.data;
        const list = Array.isArray(data?.logs) ? data.logs : [];
        setLogs(list);
      } catch (err) {
        console.warn('Error cargando logs de búsqueda:', err);
        if (isPermissionError(err) || err?.code === 'functions/permission-denied') {
          setError('Tu usuario no tiene permisos para ver las métricas de búsqueda.');
        } else {
          setError(err?.message || 'Error al cargar.');
        }
        setLogs([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, rol, authLoading]);

  const logsFiltrados = useMemo(() => {
    if (!soloNoLogueados) return logs;
    // En estos logs, los no logueados vienen con `userId` = null y normalmente con `anonymousSessionId` definido.
    return logs.filter((l) => l?.userId == null || l.userId === '');
  }, [logs, soloNoLogueados]);

  const sinResultados = logsFiltrados.filter((l) => l.resultadosEncontrados === 0);
  const pocosResultados = logsFiltrados.filter((l) => l.resultadosEncontrados > 0 && l.resultadosEncontrados <= 3);

  return (
    <div className="p-6 max-w-6xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Métricas de búsqueda</h1>
      <p className="text-sm text-gray-600 mb-6">
        Búsquedas realizadas en el buscador del Home. Sirve para ver qué buscan los usuarios y detectar oportunidades.
      </p>

      {loading && (
        <div className="flex items-center gap-2 text-gray-600 mb-4">
          <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          Cargando…
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {!loading && logsFiltrados.length > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <div className="text-sm text-gray-500">Total registros (últimos {LIMIT})</div>
              <div className="text-2xl font-bold text-gray-900">{logs.length}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <div className="text-sm text-gray-500">Sin resultados</div>
              <div className="text-2xl font-bold text-amber-600">{sinResultados.length}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <div className="text-sm text-gray-500">Pocos resultados (1–3)</div>
              <div className="text-2xl font-bold text-orange-600">{pocosResultados.length}</div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h2 className="font-semibold text-gray-900">Últimas búsquedas</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/80">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Búsqueda</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Tipo</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700">Resultados</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {logsFiltrados.map((log) => (
                    <tr key={log.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                      <td className="py-3 px-4 text-gray-900 font-medium" title={log.queryNormalizada}>
                        {log.queryOriginal || '—'}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                            log.tipoBusquedaInferida === 'marca'
                              ? 'bg-purple-100 text-purple-800'
                              : log.tipoBusquedaInferida === 'servicio'
                                ? 'bg-green-100 text-green-800'
                                : log.tipoBusquedaInferida === 'mixta'
                                  ? 'bg-blue-100 text-blue-800'
                                  : log.tipoBusquedaInferida === 'empresa'
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {log.tipoBusquedaInferida || 'libre'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        {log.resultadosEncontrados === 0 ? (
                          <span className="text-amber-600 font-medium">0</span>
                        ) : log.resultadosEncontrados <= 3 ? (
                          <span className="text-orange-600">{log.resultadosEncontrados}</span>
                        ) : (
                          <span className="text-gray-700">{log.resultadosEncontrados}</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-500">{formatDate(log.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && logsFiltrados.length === 0 && !error && (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-xl border border-gray-200">
          Aún no hay búsquedas registradas para usuarios no logueados.
        </div>
      )}

      {!loading && logsFiltrados.length > 0 && (
        <div className="mt-4 flex items-center gap-3 text-sm text-gray-700">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={soloNoLogueados}
              onChange={(e) => setSoloNoLogueados(e.target.checked)}
              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            Solo usuarios no logueados (desde Home)
          </label>
        </div>
      )}
    </div>
  );
}
